import { authenticatedResponseSchema, type AuthenticatedResponse } from '@growup/accounts-shared';
import { mediaImageSchema, type MediaImage } from '@growup/site-shared';
import type { z } from 'zod';

/**
 * Talks to the APIs behind the same origin: `/api/identity/` (sign-in),
 * `/api/members/` (youth registration) and `/api/arena/`.
 *
 * The access token (15 min) lives only in memory. The refresh token is an
 * httpOnly cookie that identity-api sets for this host; a reload or an expired
 * token asks for a new one with it (docs/contas/README.md).
 */

/** identity-api wants it on refresh and logout, so a plain cross-site form cannot use the cookie. */
const CLIENT_HEADER = { 'x-growup-client': 'admin-web' };

let accessToken: string | null = null;
const listeners = new Set<(token: string | null) => void>();

/** A failed call. `fields` maps a request field (`slug`) to its problem, when the API named one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  return accessToken;
}

export function setToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

/** Called whenever the token changes (sign-in, refresh, expiry, sign-out). */
export function onTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let refreshing: Promise<boolean> | null = null;

/** Gets a new access token from the refresh cookie. `false` means signed out. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch('/api/identity/sessions/refresh', {
        method: 'POST',
        headers: { accept: 'application/json', ...CLIENT_HEADER },
      });
      if (!response.ok) {
        setToken(null);
        return false;
      }
      setToken(authenticatedResponseSchema.parse(await response.json()).accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function signOut(): Promise<void> {
  try {
    await fetch('/api/identity/sessions/logout', {
      method: 'POST',
      headers: { authorization: `Bearer ${accessToken ?? ''}`, ...CLIENT_HEADER },
    });
  } finally {
    setToken(null);
  }
}

export function acceptSession(session: AuthenticatedResponse): void {
  setToken(session.accessToken);
}

async function toError(response: Response): Promise<ApiError> {
  let message: string | null = null;
  const fields: Record<string, string> = {};
  try {
    const body = (await response.json()) as {
      message?: unknown;
      field?: unknown;
      issues?: unknown;
    };
    if (typeof body.message === 'string') message = body.message;
    else if (Array.isArray(body.message)) message = body.message.join(' · ');
    if (typeof body.field === 'string' && message !== null) fields[body.field] = message;
    if (Array.isArray(body.issues)) {
      for (const issue of body.issues as { path?: unknown; message?: unknown }[]) {
        if (typeof issue.path === 'string' && typeof issue.message === 'string') {
          fields[issue.path] = issue.message;
        }
      }
    }
  } catch {
    // not JSON
  }
  if (message === null) {
    if (response.status === 401) message = 'A sessão expirou. Entre de novo.';
    else if (response.status === 403) message = 'Sem permissão.';
    else message = `Erro ${String(response.status)}.`;
  }
  return new ApiError(response.status, message, fields);
}

/** Unauthenticated calls (the sign-in steps). */
export async function post<S extends z.ZodTypeAny>(
  path: string,
  body: unknown,
  schema: S,
): Promise<z.output<S>> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json', ...CLIENT_HEADER },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await toError(response);
  return schema.parse(await response.json()) as z.output<S>;
}

export async function request(method: string, path: string, body?: unknown): Promise<Response> {
  const attempt = () =>
    fetch(path, {
      method,
      headers: {
        accept: 'application/json',
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  let response = await attempt();
  // An expired access token is normal every 15 minutes: renew once and retry.
  if (response.status === 401 && (await refreshSession())) response = await attempt();
  if (!response.ok) throw await toError(response);
  return response;
}

export async function getJson<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
): Promise<z.output<S>> {
  const response = await request('GET', path);
  return schema.parse(await response.json()) as z.output<S>;
}

export async function send(method: string, path: string, body?: unknown): Promise<unknown> {
  const response = await request(method, path, body);
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? (JSON.parse(text) as unknown) : null;
}

/**
 * Uploads one image to site-api (`POST /api/site/media`, docs/imagens): the
 * body is the file itself, with its own type, so no multipart is needed.
 */
export async function uploadImage(file: File): Promise<MediaImage> {
  const attempt = () =>
    fetch('/api/site/media', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': file.type,
        // Headers are ASCII; the name is only kept to recognise the file later.
        'x-file-name': encodeURIComponent(file.name),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: file,
    });
  let response = await attempt();
  if (response.status === 401 && (await refreshSession())) response = await attempt();
  if (response.status === 413) throw new ApiError(413, 'A imagem tem mais de 10 MB.');
  if (!response.ok) throw await toError(response);
  return mediaImageSchema.parse(await response.json());
}

/**
 * Uploads an event invoice or receipt (PDF, JPG or PNG, docs/eventos) to
 * site-api: like `uploadImage`, the body is the file itself.
 */
export async function uploadDocument(path: string, file: File): Promise<unknown> {
  const attempt = () =>
    fetch(path, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': file.type || 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: file,
    });
  let response = await attempt();
  if (response.status === 401 && (await refreshSession())) response = await attempt();
  if (response.status === 413) throw new ApiError(413, 'O ficheiro tem mais de 10 MB.');
  if (!response.ok) throw await toError(response);
  return (await response.json()) as unknown;
}

/** Downloads with the token (a plain link would not carry it). */
export async function download(path: string, filename: string): Promise<void> {
  const response = await request('GET', path);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** O ficheiro privado como Blob, para mostrar no ecra sem o tornar publico. */
export async function fetchBlob(path: string, type?: string): Promise<Blob> {
  const response = await request('GET', path);
  const blob = await response.blob();
  return type && blob.type !== type ? new Blob([blob], { type }) : blob;
}

export function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Erro desconhecido.';
}
